import React, { useState, useEffect } from 'react';

export default function SVGLogo({ rotation = 0, intensity = 0, mouseX = 0, mouseY = 0, distance = 0 }) {
  // Animation time for living lines
  const [animTime, setAnimTime] = useState(0);
  
  useEffect(() => {
    let animationFrameId;
    const animate = () => {
      setAnimTime(Date.now());
      animationFrameId = requestAnimationFrame(animate);
    };
    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);
  
  // Calculate line extension toward mouse
  const lineLength = 80 + (distance * 120); // Extends from 80 to 200px
  const lineAngle = Math.atan2(mouseY, mouseX) * (180 / Math.PI);
  
  return (
    <svg
      viewBox="-50 -50 500 500"
      className="w-full h-full"
      style={{
        filter: 'drop-shadow(0 0 50px rgba(0, 245, 255, 0.3))',
        overflow: 'visible'
      }}
    >
      {/* Gradients */}
      <defs>
        <radialGradient id="orbGradient" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#00f5ff" stopOpacity="0.8" />
          <stop offset="50%" stopColor="#0099cc" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#006699" stopOpacity="0.3" />
        </radialGradient>
        
        <radialGradient id="orbGlow" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#00f5ff" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#00f5ff" stopOpacity="0" />
        </radialGradient>
        
        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00f5ff" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#00f5ff" stopOpacity="0" />
        </linearGradient>
        
        <radialGradient id="coreGlow" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="30%" stopColor="#00f5ff" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#0099cc" stopOpacity="0.3" />
        </radialGradient>
      </defs>
      
      {/* Outer glow rings */}
      <circle
        cx="200"
        cy="200"
        r={140 + distance * 20}
        fill="url(#orbGlow)"
        opacity={0.2 + distance * 0.2}
      />
      
      {/* Subtle ring guides */}
      <circle
        cx="200"
        cy="200"
        r="90"
        fill="none"
        stroke="#00f5ff"
        strokeWidth="0.5"
        opacity="0.2"
      />
      
      <circle
        cx="200"
        cy="200"
        r="110"
        fill="none"
        stroke="#00f5ff"
        strokeWidth="0.3"
        opacity="0.15"
        strokeDasharray="4 8"
      />
      
      {/* Extending line - points toward mouse */}
      <g transform={`rotate(${lineAngle} 200 200)`}>
        {/* Line glow */}
        <line
          x1="200"
          y1="200"
          x2={200 + lineLength}
          y2="200"
          stroke="url(#lineGradient)"
          strokeWidth={4 + distance * 3}
          opacity={0.3 + distance * 0.4}
          strokeLinecap="round"
        />
        
        {/* Main line */}
        <line
          x1="200"
          y1="200"
          x2={200 + lineLength}
          y2="200"
          stroke="#00f5ff"
          strokeWidth={2 + distance * 2}
          opacity={0.7 + distance * 0.3}
          strokeLinecap="round"
        />
        
        {/* Line end node */}
        <circle
          cx={200 + lineLength}
          cy="200"
          r={3 + distance * 4}
          fill="#00f5ff"
          opacity={0.8}
        >
          <animate
            attributeName="r"
            values={`${3 + distance * 4};${5 + distance * 6};${3 + distance * 4}`}
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
        
        {/* End node glow */}
        <circle
          cx={200 + lineLength}
          cy="200"
          r={8 + distance * 8}
          fill="#00f5ff"
          opacity={0.2}
        >
          <animate
            attributeName="r"
            values={`${8 + distance * 8};${12 + distance * 12};${8 + distance * 8}`}
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
      </g>
      
      {/* Living Lines - organic oscillating lines - always visible */}
      {[
        { length: 50, angleOffset: 20, amplitude: 8, speed: 1.5, opacityRange: [0.3, 0.6], phase: 0 },
        { length: 70, angleOffset: -30, amplitude: 12, speed: 2.3, opacityRange: [0.2, 0.5], phase: 0.3 },
        { length: 40, angleOffset: 45, amplitude: 5, speed: 1.9, opacityRange: [0.4, 0.7], phase: 0.7 },
        { length: 65, angleOffset: -15, amplitude: 10, speed: 1.7, opacityRange: [0.3, 0.6], phase: 0.5 },
        { length: 55, angleOffset: -45, amplitude: 9, speed: 2.1, opacityRange: [0.25, 0.55], phase: 0.2 },
        { length: 45, angleOffset: 35, amplitude: 7, speed: 1.8, opacityRange: [0.35, 0.65], phase: 0.9 }
      ].map((line, i) => {
        const lineAngleWithOffset = lineAngle + line.angleOffset;
        const baseRad = (lineAngleWithOffset * Math.PI) / 180;
        
        // Calculate oscillating offset perpendicular to line direction
        const perpRad = baseRad + (Math.PI / 2);
        const oscillation = Math.sin(animTime / (line.speed * 1000) + line.phase * Math.PI * 2) * line.amplitude;
        
        const startX = 200 + Math.cos(perpRad) * oscillation;
        const startY = 200 + Math.sin(perpRad) * oscillation;
        const endX = startX + Math.cos(baseRad) * line.length;
        const endY = startY + Math.sin(baseRad) * line.length;
        
        // Pulsing opacity
        const opacityOscillation = Math.sin(animTime / (line.speed * 800) + line.phase * Math.PI) * 0.5 + 0.5;
        const opacity = line.opacityRange[0] + (line.opacityRange[1] - line.opacityRange[0]) * opacityOscillation;
        
        return (
          <g key={i}>
            {/* Line glow */}
            <line
              x1={startX}
              y1={startY}
              x2={endX}
              y2={endY}
              stroke="#00f5ff"
              strokeWidth="2"
              opacity={opacity * 0.3}
              strokeLinecap="round"
            />
            
            {/* Main line */}
            <line
              x1={startX}
              y1={startY}
              x2={endX}
              y2={endY}
              stroke="#00f5ff"
              strokeWidth="1"
              opacity={opacity}
              strokeLinecap="round"
            />
            
            {/* End node */}
            <circle
              cx={endX}
              cy={endY}
              r={1.5}
              fill="#00f5ff"
              opacity={opacity * 1.2}
            />
          </g>
        );
      })}
      
      {/* Main orb */}
      <circle
        cx="200"
        cy="200"
        r="70"
        fill="url(#orbGradient)"
        opacity={0.9}
      />
      
      {/* Orb rim highlight */}
      <circle
        cx="200"
        cy="200"
        r="70"
        fill="none"
        stroke="#00f5ff"
        strokeWidth="2"
        opacity={0.4 + intensity * 0.3}
      />
      
      {/* Inner core */}
      <circle
        cx="200"
        cy="200"
        r="50"
        fill="url(#coreGlow)"
        opacity={0.8}
      />
      
      {/* Core pulse */}
      <circle
        cx="200"
        cy="200"
        r="40"
        fill="#00f5ff"
        opacity={0.3}
      >
        <animate
          attributeName="r"
          values="35;45;35"
          dur="3s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="opacity"
          values="0.4;0.2;0.4"
          dur="3s"
          repeatCount="indefinite"
        />
      </circle>
      
      {/* Center bright point */}
      <circle
        cx="200"
        cy="200"
        r="8"
        fill="#ffffff"
        opacity={0.9}
      >
        <animate
          attributeName="r"
          values="6;10;6"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>
      
      {/* Orbiting particles along the line */}
      {distance > 0.3 && (
        <g transform={`rotate(${lineAngle} 200 200)`}>
          {[0.3, 0.5, 0.7].map((position, i) => {
            const particleX = 200 + lineLength * position;
            
            return (
              <circle
                key={i}
                cx={particleX}
                cy="200"
                r="1.5"
                fill="#00f5ff"
                opacity={0.6}
              >
                <animate
                  attributeName="cy"
                  values="200;195;200;205;200"
                  dur={`${1.5 + i * 0.3}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.4;0.8;0.4"
                  dur={`${1.5 + i * 0.3}s`}
                  repeatCount="indefinite"
                />
              </circle>
            );
          })}
        </g>
      )}
      
      {/* Corner tech details */}
      {[0, 90, 180, 270].map((angle) => {
        const rad = (angle * Math.PI) / 180;
        const x = 200 + Math.cos(rad) * 130;
        const y = 200 + Math.sin(rad) * 130;
        
        return (
          <g key={angle} opacity="0.2">
            <line
              x1={x - 5}
              y1={y}
              x2={x + 5}
              y2={y}
              stroke="#00f5ff"
              strokeWidth="0.5"
            />
            <line
              x1={x}
              y1={y - 5}
              x2={x}
              y2={y + 5}
              stroke="#00f5ff"
              strokeWidth="0.5"
            />
          </g>
        );
      })}
      
    </svg>
  );
}
